import React, { useState, useEffect } from 'react';
import { DateRangePicker } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import {
    Modal,
    Box,
    Typography,
    Button,
    Stack,
    List,
    ListItem,
    ListItemText,
    IconButton,
    TextField,
    Autocomplete,
    Grid,
    Divider,
    Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import useApi from '../../../shared/hooks/useApi';
import { DateRange } from 'react-date-range';
import LoadingScreen from '../../../shared/components/LoadingScreen/LoadingScreen';
import './DailyPlannerModal.css';
import dayjs from 'dayjs';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
// or import 'dayjs/locale/fr-be'; // For Belgian French

// Initialize dayjs plugins
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.tz.setDefault('Europe/Brussels');

const DailyPlannerModal = ({ open, onClose, setLoading, triggerAlert }) => {

    const [state, setState] = useState([
        {
            startDate: new Date(),
            endDate: new Date(),
            key: 'selection'
        }
    ]);
    const [startTime, setStartTime] = useState(dayjs().startOf('day'));
    const [endTime, setEndTime] = useState(dayjs().endOf('day').minute(0));

    const [unavailabilities, setUnavailabilities] = useState([]);
    const { apiPost, apiGet, apiDelete, loadingApi, errorPosting } = useApi();
    const [unavailableDates, setUnavailableDates] = useState([]);
    const [shutdownDates, setShutdownDates] = useState([]);
    const [maintenanceDates, setMaintenanceDates] = useState([]);
    const [safetyInspectionDates, setSafetyInspectionDates] = useState([]);
    const [reason, setReason] = useState('');
    const [asset, setAsset] = useState('CHP01');
    const [isAddClicked, setIsAddClicked] = useState(false);
    // Fetch existing unavailabilities
    const startDate = dayjs(state[0].startDate).tz('Europe/Brussels');
    const endDate = dayjs(state[0].endDate).tz('Europe/Brussels');
    const handleAddClick = () => {
        setIsAddClicked(true);
    }

    const handleSaveClick = () => {
        if (!reason || !asset) {
            triggerAlert("error", "Reason and Asset are required");
            return;
        }
        if (startDate.isBefore(earliestUnavailabilityDateForModal)) {
            triggerAlert("error", "Start date is before the earliest unavailability date");
            return;
        }

        try {
            const startDateTime = dayjs(state[0].startDate)
                .hour(startTime.hour())
                .minute(startTime.minute());
            const endDateTime = dayjs(state[0].endDate)
                .hour(endTime.hour())
                .minute(endTime.minute());

            handleSavePlanning({
                start: startDateTime.toISOString(),
                stop: endDateTime.toISOString(),
                asset: asset,
                reason: reason
            });
            setIsAddClicked(false);
        } catch (error) {
            triggerAlert("error", "Error saving planning");

        }
    }
    const getEarliestUnavailabilityDate = () => {
        const now = dayjs();
        const today = now.startOf('day');
        const eightAM = today.add(8, 'hour');
        if (now.isBefore(eightAM)) {
            // Before 8 AM, allow starting from the next day
            return today.add(1, 'day').startOf('day');
        } else {
            // After 8 AM, allow starting from the day after the next
            return today.add(2, 'day').startOf('day');
        }
    };
    const getEarliestUnavailabilityDateForModal = () => {
        const now = dayjs().tz('Europe/Brussels', true);
        const today = now.startOf('day');
        const eightAM = today.add(8, 'hour');

        if (now.isBefore(eightAM)) {
            return today.add(1, 'day').tz('Europe/Brussels', true);
        } else {
            return today.add(2, 'day').tz('Europe/Brussels', true);
        }
    };

    useEffect(() => {
        setState([{
            ...state[0],
            startDate: earliestUnavailabilityDateForModal.toDate(),
            endDate: earliestUnavailabilityDateForModal.toDate()
        }]);
    }, []);



    const earliestUnavailabilityDate = getEarliestUnavailabilityDate();
    const earliestUnavailabilityDateForModal = getEarliestUnavailabilityDateForModal();

    const fetchUnavailabilities = async () => {
        try {
            setLoading(true);
            const response = await apiGet('UX_HTTP_API', '/esid-24-031/list-scheduled-unavailabilities');
            const formattedUnavailabilities = response.unavailabilities.map(item => ({
                startDate: new Date(item.start),
                endDate: new Date(item.stop),
                key: `${item.key}`,
                //color: item.asset === 'Eboiler' ? '#FFA726' : "All" ? '#483F5E' : '#EF5350',
                color: item.reason === 'Plant Shutdown' ? '#ef5350' :
                    item.reason === 'Planned Maintenance' ? '#FFA726' :
                        item.reason === 'Safety Inspection' ? '#483F5E' : '#ef5350',

                asset: item.asset,
                reason: item.reason
            }));
            setShutdownDates(formattedUnavailabilities.filter(item => item.reason === 'Plant Shutdown'))
            setMaintenanceDates(formattedUnavailabilities.filter(item => item.reason === 'Planned Maintenance'))
            setSafetyInspectionDates(formattedUnavailabilities.filter(item => item.reason === 'Safety Inspection'))
            let unavailableDates = []

            for (const item of formattedUnavailabilities) {
                const startDate = new Date(item.startDate);
                const endDate = new Date(item.endDate);
                const date = new Date(startDate);
                while (date <= endDate) {
                    unavailableDates.push(new Date(date));
                    date.setDate(date.getDate() + 1);
                }
            }
            setUnavailableDates(unavailableDates);
            setUnavailabilities(formattedUnavailabilities);
            setLoading(false);
        } catch (err) {
            console.error('Error:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (open) {
            fetchUnavailabilities();
        }
    }, [open]);


    const isDateDisabled = (date) => {
        const compareDateOnly = (dateA, dateB) => {
            return new Date(dateA.getFullYear(), dateA.getMonth(), dateA.getDate());
        };

        return unavailabilities.some(unavailable => {
            const checkDate = compareDateOnly(new Date(date));
            const startDate = compareDateOnly(unavailable.startDate);
            const endDate = compareDateOnly(unavailable.endDate);

            return checkDate >= startDate && checkDate <= endDate;
        });
    };
    const handleClose = () => {
        setIsAddClicked(false);
    }
    // Function to get the tooltip text for disabled dates
    const getDisabledTooltip = (date) => {
        const unavailable = unavailabilities.find(item => {
            const checkDate = new Date(date);
            return checkDate >= item.startDate &&
                checkDate <= item.endDate;
        });
        return unavailable ? `${unavailable.asset}: ${unavailable.reason}` : '';
    };
    const handleSavePlanning = async (planningData) => {
        try {
            setLoading(true);
            // Handle saving the planning data
            const payload = {
                unavailabilities: [
                    { ...planningData }
                ]
            };
            await apiPost('UX_HTTP_API', `/esid-24-031/add-scheduled-unavailability`, payload);
            triggerAlert("success", "Planning saved successfully");
            //onClose();
        } catch (error) {
            triggerAlert("error", "Error saving planning");
            setLoading(false);
        } finally {
            setLoading(false);
            fetchUnavailabilities();

        }
    };
    const handleDeleteUnavailability = async (item, key) => {
        try {
            setLoading(true);
            const queryStringParameters = {
                project_id: 'ESID-24-031',
                key: key
            };

            await apiDelete(
                'UX_HTTP_API',
                `/esid-24-031/delete-scheduled-unavailability`,
                queryStringParameters
            );
            triggerAlert("success", `${item?.asset} - ${item?.reason} deleted successfully`);
            // Refresh the list after successful deletion
            await fetchUnavailabilities();
            setLoading(false);
        } catch (error) {
            triggerAlert("error", "Error deleting unavailability");
            console.error('Error deleting unavailability:', error);
        }
    }

    const isSameOrBetweenDates = (date, startDate, endDate) => {
        // Reset time part to compare only date
        const compareDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        const start = new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate());
        const end = new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate());

        return compareDate >= start && compareDate <= end;
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >

            <Box sx={{
                bgcolor: 'background.paper',
                borderRadius: 2,
                boxShadow: 24,
                p: 4,
                width: '60vw',
                maxWidth: 900,
                height: '90vh',
                overflow: 'auto'
            }}>
                <Stack spacing={3}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h5" sx={{ fontWeight: "bold" }}>Schedule Planning</Typography>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Grid container spacing={2} justifyContent="center" alignContent={"center"} alignItems={"center"} sx={{ height: "70vh" }}>

                            <Grid item xs={6}  >
                                <Paper sx={paperStyle}>
                                    <Grid item xs={12} sx={{
                                        justifyContent: "center",
                                        alignItems: "center",
                                        alignContent: "center",
                                        display: "flex",
                                        flexDirection: "column",
                                        minHeight: "50vh",
                                        maxHeight: "70vh",
                                    }}>
                                        {unavailabilities && (
                                            <Box>
                                                {unavailabilities?.length > 0 && <List dense sx={{ maxHeight: "50vh", width: 300, overflow: "auto" }}>
                                                    {unavailabilities.map((item, index) => (
                                                        <ListItem
                                                            key={index}
                                                            sx={{
                                                                bgcolor: `${item.color}20`,
                                                                borderRadius: 1,
                                                                mb: 1,
                                                                boxShadow: "0px 0px 3px 0px rgba(0, 0, 0, 0.1)",
                                                                border: `1px solid ${item.color}40`
                                                            }}
                                                        >
                                                            <ListItemText
                                                                primary={`${item.asset} - ${item.reason}`}
                                                                secondary={`${item.startDate.toLocaleString('nl-NL', {
                                                                    day: '2-digit',
                                                                    month: '2-digit',
                                                                    year: 'numeric',
                                                                    hour: '2-digit',
                                                                    minute: '2-digit'
                                                                })} - ${item.endDate.toLocaleString('nl-NL', {
                                                                    day: '2-digit',
                                                                    month: '2-digit',
                                                                    year: 'numeric',
                                                                    hour: '2-digit',
                                                                    minute: '2-digit'
                                                                })}`}
                                                            />
                                                            <IconButton onClick={() => handleDeleteUnavailability(item, item.key)}>
                                                                <DeleteIcon />
                                                            </IconButton>
                                                        </ListItem>
                                                    ))}



                                                </List>}

                                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: "column" }}>
                                                    <Button
                                                        onClick={() => isAddClicked ? handleSaveClick() : handleAddClick()}
                                                        variant="inherit" color="primary" sx={{ mt: 2, borderRadius: 1, border: "1px dashed #008000" }}>
                                                        Add New Unavailability
                                                    </Button>
                                                    {/* make this dd/mm/yyyy */}
                                                    <Typography variant="subtitle" color="text.secondary"
                                                        gutterBottom>{state[0]?.startDate?.toLocaleDateString('nl-NL', {
                                                            day: '2-digit',
                                                            month: '2-digit',
                                                            year: 'numeric'
                                                        })} - {state[0]?.endDate?.toLocaleDateString('nl-NL', {
                                                            day: '2-digit',
                                                            month: '2-digit',
                                                            year: 'numeric'
                                                        })}</Typography>
                                                </Box>
                                            </Box>
                                        )}
                                    </Grid>
                                </Paper>

                            </Grid>

                            <Grid item xs={6} >
                                <Paper sx={paperStyle} >

                                    <Grid item xs={12} sx={{

                                        justifyContent: "center",
                                        alignItems: "center",
                                        alignContent: "center",
                                        display: "flex",
                                        flexDirection: "column",
                                        minHeight: "50vh",
                                        maxHeight: "70vh",
                                        ml: 2,
                                        mr: 2,
                                    }}>
                                        <DateRange
                                            editableDateInputs={true}
                                            onChange={item => setState([item.selection])}
                                            moveRangeOnFirstSelection={false}
                                            ranges={state}
                                            sx={{
                                                border: "1px solid red",
                                                borderRadius: 1,
                                                p: 2
                                            }}
                                            disabledDates={unavailableDates}
                                            minDate={earliestUnavailabilityDate.toDate()} // Set the minimum date
                                            dayContentRenderer={date => {
                                                const isDisabled = isDateDisabled(date);
                                                const isDateShutdown = isDisabled && shutdownDates.some(item =>
                                                    isSameOrBetweenDates(date, item.startDate, item.endDate)
                                                );
                                                const isDateMaintenance = isDisabled && maintenanceDates.some(item =>
                                                    isSameOrBetweenDates(date, item.startDate, item.endDate)
                                                );
                                                const isDateSafetyInspection = isDisabled && safetyInspectionDates.some(item =>
                                                    isSameOrBetweenDates(date, item.startDate, item.endDate)
                                                );
                                                const isPastDate = date < new Date(new Date().setHours(0, 0, 0, 0)); // Check if date is in the past
                                                return (
                                                    <div
                                                        style={{
                                                            position: 'relative',
                                                            height: '100%',
                                                            width: '100%',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            backgroundColor: isPastDate ? '#f0f0f0' : isDisabled ? isDateShutdown ? '#ef535020' : isDateMaintenance ? '#FFA72620' : isDateSafetyInspection ? '#483F5E20' : 'transparent' : "transparent",
                                                            color: isDisabled ? 'gray' : 'black',
                                                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                                                        }}
                                                        title={isDisabled ? getDisabledTooltip(date) : ''}
                                                    >
                                                        {date.getDate()}
                                                        {isDisabled && (
                                                            <div
                                                                style={{
                                                                    position: 'absolute',
                                                                    bottom: 2,
                                                                    width: 4,
                                                                    height: 4,
                                                                    borderRadius: '50%',
                                                                    backgroundColor: isDateShutdown ? '#ef5350' : isDateMaintenance ? '#FFA726' : isDateSafetyInspection ? '#483F5E' : 'gray'
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                );
                                            }}
                                        />
                                    </Grid>
                                </Paper>

                            </Grid>

                        </Grid>



                    </Box>
                    {isAddClicked && (
                        <Modal
                            open={isAddClicked}
                            onClose={handleClose}
                            aria-labelledby="modal-modal-title"
                            aria-describedby="modal-modal-description"
                        >
                            <Box sx={style}>
                                <Box>

                                    <Box sx={{ display: 'flex', width: "100%", justifyContent: 'center', alignItems: 'center' }}>
                                        <Typography variant="h5" sx={{ fontWeight: "bold", mb: 2 }}>Select Reason and Asset</Typography>

                                    </Box>
                                    <LocalizationProvider
                                        dateAdapter={AdapterDayjs}
                                        adapterLocale="nl-be"
                                        timeZone="Europe/Brussels"
                                    >
                                        <List dense>
                                            {/* Start Date and Time Row */}
                                            <ListItem sx={{ mb: 2 }}>
                                                <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                                                    <DatePicker
                                                        label="Start Date"
                                                        value={dayjs(state[0].startDate).tz('Europe/Brussels')}
                                                        onChange={(newValue) => {
                                                            if (newValue) {
                                                                setState([{
                                                                    ...state[0],
                                                                    startDate: newValue.tz('Europe/Brussels').toDate(),
                                                                    endDate: dayjs(state[0].endDate).isBefore(newValue)
                                                                        ? newValue.tz('Europe/Brussels').toDate()
                                                                        : state[0].endDate
                                                                }]);
                                                            }
                                                        }}
                                                        minDate={earliestUnavailabilityDateForModal} // Set the minimum date
                                                        sx={{ flex: 2 }}
                                                        format="DD/MM/YYYY"
                                                    />
                                                    <TimePicker
                                                        label="Start Time"
                                                        value={startTime}
                                                        onChange={(newValue) => {
                                                            if (newValue) {
                                                                // Set minutes to 00 for start time
                                                                const timeWithZeroMinutes = newValue.tz('Europe/Brussels').minute(0);
                                                                setStartTime(timeWithZeroMinutes);
                                                                if (dayjs(state[0].startDate).isSame(state[0].endDate, 'day') &&
                                                                    endTime.isBefore(timeWithZeroMinutes)) {
                                                                    setEndTime(timeWithZeroMinutes);
                                                                }
                                                            }
                                                        }}
                                                        sx={{ flex: 1 }}
                                                        ampm={false}
                                                        format="HH:mm"
                                                        minutesStep={60}
                                                    />
                                                </Box>
                                            </ListItem>

                                            {/* End Date and Time Row */}
                                            <ListItem sx={{ mb: 2 }}>
                                                <Box sx={{ display: 'flex', gap: 2, width: '100%' }}>
                                                    <DatePicker
                                                        label="End Date"
                                                        value={dayjs(state[0].endDate).tz('Europe/Brussels')}
                                                        onChange={(newValue) => {
                                                            if (newValue) {
                                                                setState([{
                                                                    ...state[0],
                                                                    endDate: newValue.tz('Europe/Brussels').toDate()
                                                                }]);
                                                            }
                                                        }}
                                                        minDate={dayjs(state[0].startDate)}
                                                        sx={{ flex: 2 }}
                                                        format="DD/MM/YYYY"
                                                    />
                                                    <TimePicker
                                                        label="End Time"
                                                        value={endTime}
                                                        onChange={(newValue) => {
                                                            if (newValue) {
                                                                // Set minutes to 00 for end time
                                                                const timeWithZeroMinutes = newValue.tz('Europe/Brussels').minute(0);
                                                                setEndTime(timeWithZeroMinutes);
                                                            }
                                                        }}
                                                        sx={{ flex: 1 }}
                                                        ampm={false}
                                                        format="HH:mm"
                                                        minutesStep={60}
                                                    />
                                                </Box>
                                            </ListItem>
                                            <ListItem
                                                key={"Reason"}
                                                sx={{
                                                    borderRadius: 1,
                                                    mb: 2
                                                }}
                                            >
                                                <TextField sx={{ width: "100%" }} value={reason} onChange={(event) => setReason(event.target.value)} label="Reason" />
                                            </ListItem>
                                            <ListItem
                                                key={"Asset"}
                                                sx={{
                                                    borderRadius: 1,
                                                    mb: 1,
                                                    width: "100%"
                                                }}
                                            >
                                                <Autocomplete
                                                    options={['CHP01']}
                                                    value={asset}
                                                    onChange={(event, newValue) => {
                                                        setAsset(newValue);
                                                    }}
                                                    sx={{ width: "100%" }}
                                                    renderInput={(params) => <TextField {...params} label="Asset" />}
                                                />
                                            </ListItem>
                                        </List>
                                    </LocalizationProvider>
                                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
                                        <Button variant="outlined" onClick={handleClose} sx={{ width: 80 }}>Cancel</Button>
                                        <Button variant="outlined" onClick={handleSaveClick} sx={{ bgcolor: '', width: 80 }}>Save</Button>
                                    </Box>
                                </Box>
                            </Box>
                        </Modal>
                    )}
                </Stack>
            </Box>
        </Modal>
    );
};

export default DailyPlannerModal;


const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: 2,
    textAlign: "center",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center"
};

const paperStyle = {
    p: 2,
    height: 500,
    justifyContent: "center",
    alignItems: "center",
    alignContent: "center",
    display: "flex",
    flexDirection: "column"
}
